import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';

import { obtenerBalance, limpiarBalance } from '../../../actions/pagosVentas';

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-MX');
};

const formatMoney = (n) => {
    const v = Number(n || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v);
};

const monedaTextoVenta = (venta) => (Number(venta?.moneda) === 1 ? 'USD' : 'MXN');

const renderBadgeStatusPago = (status) => {
    const s = Number(status);
    if (s === 0) return <span className="badge bg-danger">No pagada</span>;
    if (s === 1) return <span className="badge text-dark" style={{ backgroundColor: '#fd7e14' }}>Parcial</span>;
    if (s === 2) return <span className="badge bg-success">Pagada</span>;
    return <span className="badge bg-secondary">Sin definir</span>;
};

const paginationOptions = {
    rowsPerPageText: 'Filas por página',
    rangeSeparatorText: 'de',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Todos'
};

const VentasCliente = ({ idCliente }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { ventas, loadingBalance } = useSelector(state => state.pagosVentas);

    useEffect(() => {
        if (!idCliente) return;

        dispatch(obtenerBalance(idCliente));

        return () => {
            dispatch(limpiarBalance());
        };
    }, [dispatch, idCliente]);

    const columns = useMemo(() => ([
        {
            name: 'Folio',
            selector: row => row?.folio || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1,
            cell: row => (
                <button
                    type="button"
                    className="btn btn-sm btn-link p-0"
                    onClick={() => navigate(`/ventas/editar/${row._id}`)}
                >
                    {row?.folio || 'Sin definir'}
                </button>
            )
        },
        {
            name: 'Fecha',
            selector: row => row?.fecha ? new Date(row.fecha).getTime() : 0,
            sortable: true,
            wrap: true,
            grow: 1,
            cell: row => formatDate(row?.fecha)
        },
        {
            name: 'Total',
            selector: row => row?.total || 0,
            sortable: true,
            right: true,
            cell: row => <div style={{ width: '100%', textAlign: 'right' }}>$ {formatMoney(row?.total)}</div>,
            grow: 1
        },
        {
            name: 'Moneda',
            selector: row => monedaTextoVenta(row),
            sortable: true,
            center: true,
            cell: row => (
                <span className={`badge ${Number(row?.moneda) === 1 ? 'bg-info text-dark' : 'bg-light text-dark border'}`}>
                    {monedaTextoVenta(row)}
                </span>
            ),
            grow: 1
        },
        {
            name: 'Pagado (MXN)',
            selector: row => row?.monto_pagado || 0,
            sortable: true,
            right: true,
            cell: row => <div style={{ width: '100%', textAlign: 'right' }}>$ {formatMoney(row?.monto_pagado)}</div>,
            grow: 1
        },
        {
            name: 'Saldo (MXN)',
            selector: row => row?.saldo || 0,
            sortable: true,
            right: true,
            cell: row => <div style={{ width: '100%', textAlign: 'right' }}>$ {formatMoney(row?.saldo)}</div>,
            grow: 1
        },
        {
            name: 'Status',
            width: '130px',
            cell: row => renderBadgeStatusPago(row?.status_pago)
        }
    ]), [navigate]);

    return (
        <div className="mt-2">
            <DataTable
                columns={columns}
                data={ventas || []}
                progressPending={loadingBalance && (!ventas || ventas.length === 0)}
                defaultSortFieldId={2}
                defaultSortAsc={false}
                pagination
                paginationComponentOptions={paginationOptions}
                highlightOnHover
                striped
                responsive
                persistTableHead
                noDataComponent="Sin ventas registradas"
            />
        </div>
    );
};

export default VentasCliente;
